import React from "react";
import { AdminFormFieldSubmit } from "../../../../components/admin/formFields";
import {
  CameraDetail,
  DeviceReportSimple,
  DeviceReportSummary,
} from "../../../../api/types/CamerasAdminVM";
import { useGetDeviceICCHistoryQuery } from "../../../../api/network/vmApiService";
import Check from "../../../../assets/svg/check-ring.svg";
import Unhealthy from "../../../../assets/svg/unhealthy.svg";
import { useTranslation } from "react-i18next";
import Tooltip from "../../../../components/cameraAdmin/tooltip";
import ModalICCHistory from "../modalICCHistory";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../../api/store/commonSlice";
import { useAccountTimezone } from "../../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../../utils/dateTime";
import { APP_CONFIG } from "../../../../constants/constants";

interface ScreenCameraProps {
  cameraData: CameraDetail | null;
  reportData: DeviceReportSummary | null;
  simpleHealthData: DeviceReportSimple | null;
}

const CameraSim = (props: ScreenCameraProps) => {
  const { cameraData, reportData, simpleHealthData } = props;
  const healthReport = reportData?.report;
  const healthGeneral = healthReport?.health_general;
  const simpleReportData = simpleHealthData?.report;

  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras.camera_details.tabs.sim",
  });
  const { tzForDateFns } = useAccountTimezone();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const deviceId = reportData?.device_id;
  const { data: dataICCHistoryResponse } = useGetDeviceICCHistoryQuery(
    deviceId?.toString() ?? ""
  );
  const historyData = dataICCHistoryResponse?.data ?? [];

  const LeftInfoItems = [
    {
      label: "SIM ICCID : ",
      value: `${reportData?.report?.health_icc?.sim_type?.status} / ${reportData?.report?.health_icc?.id?.status?.toString()?.replace("ICC_ID:", "")}`,
    },
    {
      label: "Location Function",
      type: "status",
      status: simpleReportData?.location_function?.status,
      description: `${simpleReportData?.location_function?.description} ${simpleReportData?.location_function.solution}`,
    },
    {
      label: "Time Taken to Get GPS Fix After Boot Complete",
      value:
        healthReport?.health_times?.first_gps_fixed_taken_time_second?.status &&
        healthReport?.health_times?.first_gps_fixed_taken_time_second?.status?.toString() !==
          "-"
          ? `${healthReport?.health_times?.first_gps_fixed_taken_time_second?.status} Sec`
          : "-",
    },
    {
      label: "Sensor Events Detection",
      type: "status",
      status: simpleReportData?.sensor_events_detection?.status,
      description: `${simpleReportData?.sensor_events_detection?.description} ${simpleReportData?.sensor_events_detection.solution}`,
    },
    {
      label: "Device I/O Cover",
      value: healthGeneral?.io_cover_status?.status,
    },
    {
      label: "IMEI",
      value: cameraData?.IMEI_1 ?? cameraData?.IMEI_2 ?? "",
    },
  ];

  const RightInfoItems = [
    {
      label: "Boot Complete Timestamp",
      value:
        formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(
            healthReport?.health_times?.latest_system_boot_timestamp?.status ??
              0
          ),
          tzForDateFns,
          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
        ) || "-",
    },
    {
      label: "Device Shutdown Timestamp",
      value:
        formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(
            healthReport?.health_times?.latest_system_shutdown_timestamp
              ?.status ?? 0
          ),
          tzForDateFns,
          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
        ) || "-",
    },
    {
      label: "ACC/Ignition On Timestamp",
      value:
        formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(
            healthReport?.health_times?.latest_acc_on_timestamp?.status ?? 0
          ),
          tzForDateFns,
          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
        ) || "-",
    },
    {
      label: "ACC/Ignition Off Timestamp",
      value:
        formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(
            healthReport?.health_times?.latest_acc_off_timestamp?.status ?? 0
          ),
          tzForDateFns,
          APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
        ) || "-",
    },
    {
      label: "Voltage Read at Last Acc Off",
      value: healthReport?.health_battery?.voltage?.status
        ? `${healthReport?.health_battery?.voltage?.status} V`
        : "-",
    },
  ];

  const toggleICCIDModal = () =>
    dispatch(setModalsData({ ...modalsState, showModalVMICChistory: true }));

  return (
    <div className="col-span-9">
      <div className="flex flex-row items-center justify-between">
        <h1 className="font-semibold text-base leading-none align-middle">
          {t("heading")}
        </h1>
        <AdminFormFieldSubmit
          buttonDimensionClass={"h-10 w-32 px-6"}
          type="button"
          variant="primary"
          label={
            <span className="flex justify-center whitespace-nowrap">
              {t("icc_history")}
            </span>
          }
          onClick={toggleICCIDModal}
        />
      </div>
      <div className="bg-gray-100 w-full flex rounded-lg p-4 mt-2">
        <div className="w-1/2 px-4 pt-2 space-y-4">
          {LeftInfoItems.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span className="font-semibold text-sm">{item.label}</span>
              {item?.type && item?.type === "status" ? (
                <div className="flex flex-row items-center">
                  <img
                    src={item?.status === "healthy" ? Check : Unhealthy}
                    className="h-5 w-5 mr-2"
                    alt="health"
                  />
                  <span className="font-medium text-sm">
                    {item?.status === "healthy" ? "Healthy" : "Unhealthy"}
                  </span>
                  {item?.status === "unhealthy" && (
                    <Tooltip description={item?.description} />
                  )}
                </div>
              ) : (
                <span className="text-sm">{item.value}</span>
              )}
            </div>
          ))}
        </div>
        <div className="border-r border-gray-200 h-auto mx-2" />
        <div className="w-1/2 px-4 pt-2 space-y-4">
          {RightInfoItems.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span className="font-semibold text-sm">{item.label}</span>
              <span className="text-sm">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
      <ModalICCHistory historyData={historyData} />
    </div>
  );
};

export default CameraSim;
